import { overridable } from '@wix/santa-editor-utils';
import * as highlightsActions from '../highlights/highlightsActions';
import * as servicesActions from '../services/servicesActions';
import * as domMeasurements from '../domMeasurements/domMeasurements';
import { platform } from '#packages/util';

import type { EditorAPI } from '@/editorAPI';
import type { CompLayout, CompRef } from '@wix/document-services-types';
import type { StageRect } from '../domMeasurements/domMeasurements.types';
import type { Dispatch } from '../../../types/redux';
import type { EditorState } from '../store/editorState';

const MARGIN_FROM_SITE_EDGE = 30;

const { getStageRect } = domMeasurements.selectors;

const getScrollCoordinateToShowComponent = (
  layout: CompLayout,
  stageRect: StageRect,
) => {
  const componentTopIsAboveViewport = layout.y < stageRect.top;
  const componentTopIsBelowViewport = layout.y > stageRect.bottom;
  const componentTopIsVisible =
    layout.y > stageRect.top && layout.y < stageRect.bottom;
  const componentBottomIsVisible = layout.y + layout.height < stageRect.bottom;
  const componentIsBiggerThenViewport =
    layout.height > stageRect.bottom - stageRect.top;

  const getScrollTopCoordinate = (compLayout: CompLayout) =>
    Math.max(compLayout.y - MARGIN_FROM_SITE_EDGE, 0);
  const getScrollBottomCoordinate = (
    compLayout: CompLayout,
    viewport: StageRect,
  ) =>
    viewport.top +
    (compLayout.y - viewport.bottom) +
    compLayout.height +
    MARGIN_FROM_SITE_EDGE;

  if (componentTopIsAboveViewport) {
    return getScrollTopCoordinate(layout);
  } else if (componentTopIsVisible && componentIsBiggerThenViewport) {
    return getScrollTopCoordinate(layout);
  } else if (
    componentTopIsVisible &&
    !componentBottomIsVisible &&
    !componentIsBiggerThenViewport
  ) {
    return getScrollBottomCoordinate(layout, stageRect);
  } else if (componentTopIsBelowViewport && !componentIsBiggerThenViewport) {
    return getScrollBottomCoordinate(layout, stageRect);
  } else if (componentTopIsBelowViewport && componentIsBiggerThenViewport) {
    return getScrollTopCoordinate(layout);
  }

  return stageRect.top;
};

const locateComponentAndHighlight = overridable(
  (compRef: CompRef, isHover: boolean, type?: string) =>
    (dispatch: Dispatch) => {
      dispatch(
        locateComponentAndHighlightMultiComponents([compRef], isHover, type),
      );
      return;
    },
);

const locateComponentAndHighlightMultiComponents = overridable(
  (compRefs: CompRef[], isHover: boolean, type?: string) =>
    (
      dispatch: Dispatch,
      getState: () => EditorState,
      { editorAPI }: { editorAPI: EditorAPI },
    ) => {
      const compRefsWithRepeaters =
        platform.renderUtils.getCompRefIncludingRepeatedItems(
          editorAPI,
          compRefs,
        );

      const compRefsAndLayouts = compRefsWithRepeaters.map((compRef) => ({
        compRef,
        layout: editorAPI.components.layout.getRelativeToScreen(compRef),
      }));
      dispatch(
        highlightsActions.handleMultiComponentHighlight(
          compRefsAndLayouts,
          isHover,
          type,
        ),
      );

      if (compRefsAndLayouts.length > 0) {
        const stageRect = getStageRect(getState());
        const newScrollPosition = getScrollCoordinateToShowComponent(
          compRefsAndLayouts[0].layout,
          stageRect,
        );

        if (newScrollPosition !== stageRect.top) {
          dispatch(
            servicesActions.setState({
              animateScrollTo: {
                position: { x: 0, y: newScrollPosition },
                duration: 0.5,
              },
            }),
          );
        }
      }
    },
);

export {
  locateComponentAndHighlight,
  locateComponentAndHighlightMultiComponents,
};
